/**
 * 简单事件总线
 * 薄DDD方案 - 用于模块间解耦通信
 */
export type EventHandler<T = any> = (data: T) => void | Promise<void>;

/**
 * 事件总线类
 */
export class EventBus {
  private static instance: EventBus;
  private events: Map<string, Set<EventHandler>> = new Map();

  private constructor() {}

  /**
   * 获取单例实例
   */
  static getInstance(): EventBus {
    if (!EventBus.instance) {
      EventBus.instance = new EventBus();
    }
    return EventBus.instance;
  }

  /**
   * 订阅事件
   */
  on<T = any>(eventName: string, handler: EventHandler<T>): () => void {
    if (!this.events.has(eventName)) {
      this.events.set(eventName, new Set());
    }

    const handlers = this.events.get(eventName)!;
    handlers.add(handler);

    // 返回取消订阅函数
    return () => {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.events.delete(eventName);
      }
    };
  }

  /**
   * 取消订阅事件
   */
  off<T = any>(eventName: string, handler: EventHandler<T>): void {
    const handlers = this.events.get(eventName);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.events.delete(eventName);
      }
    }
  }

  /**
   * 发布事件（同步）
   */
  emit<T = any>(eventName: string, data?: T): void {
    const handlers = this.events.get(eventName);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for "${eventName}":`, error);
        }
      });
    }
  }

  /**
   * 发布事件（异步）
   */
  async emitAsync<T = any>(eventName: string, data?: T): Promise<void> {
    const handlers = this.events.get(eventName);
    if (handlers) {
      const promises = Array.from(handlers).map(async (handler) => {
        try {
          await handler(data);
        } catch (error) {
          console.error(
            `Error in async event handler for "${eventName}":`,
            error,
          );
        }
      });

      await Promise.all(promises);
    }
  }

  /**
   * 清除所有事件监听器
   */
  clear(): void {
    this.events.clear();
  }

  /**
   * 清除指定事件的所有监听器
   */
  clearEvent(eventName: string): void {
    this.events.delete(eventName);
  }

  /**
   * 获取事件监听器数量
   */
  listenerCount(eventName: string): number {
    return this.events.get(eventName)?.size || 0;
  }
}

/**
 * 导出单例实例
 */
export const eventBus = EventBus.getInstance();

/**
 * 常见领域事件定义
 */
export const DomainEvents = {
  // 患者相关事件
  PATIENT_CREATED: "patient:created",
  PATIENT_UPDATED: "patient:updated",
  PATIENT_DELETED: "patient:deleted",
  PATIENT_ADMITTED: "patient:admitted",
  PATIENT_DISCHARGED: "patient:discharged",

  // 病历相关事件
  RECORD_CREATED: "record:created",
  RECORD_UPDATED: "record:updated",
  RECORD_DELETED: "record:deleted",
  RECORD_SIGNED: "record:signed",

  // 队列相关事件
  QUEUE_PATIENT_ADDED: "queue:patient_added",
  QUEUE_PATIENT_REMOVED: "queue:patient_removed",
  QUEUE_PATIENT_CALLED: "queue:patient_called",

  // 标签相关事件
  TAG_CREATED: "tag:created",
  TAG_UPDATED: "tag:updated",
  TAG_DELETED: "tag:deleted",
  TAG_ASSIGNED: "tag:assigned",
} as const;

/**
 * 事件数据类型
 */
export interface PatientEventData {
  patientId: string;
  patientName?: string;
  timestamp: Date;
}

export interface RecordEventData {
  recordId: string;
  patientId: string;
  timestamp: Date;
}

export interface QueueEventData {
  patientId: string;
  queueType: string;
  timestamp: Date;
}
